// Brief confirmations with Undo (the pattern X and Gmail use for reversible actions): one at a time,
// bottom centre, announced politely, gone after five seconds or when Undo is pressed. Destructive,
// irreversible actions still ask first; reversible ones just offer Undo.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";
import s from "./Toast.module.css";

type Toast = { id: number; text: string; undo?: () => void };
type ShowToast = (text: string, undo?: () => void) => void;

const ToastContext = createContext<ShowToast>(() => {});
export const useToast = () => useContext(ToastContext);

/** From outside React (the query client): show why something was refused. */
const ERROR_EVENT = "bs:error";
export const showError = (text: string) => window.dispatchEvent(new CustomEvent<string>(ERROR_EVENT, { detail: text }));

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const show = useCallback<ShowToast>((text, undo) => {
    clearTimeout(timer.current);
    setToast({ id: Date.now(), text, undo });
    timer.current = setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    const onError = (e: Event) => show((e as CustomEvent<string>).detail);
    window.addEventListener(ERROR_EVENT, onError);
    return () => window.removeEventListener(ERROR_EVENT, onError);
  }, [show]);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={s.region} role="status" aria-live="polite">
        {toast && (
          <div key={toast.id} className={s.toast}>
            <span>{toast.text}</span>
            {toast.undo && (
              <button
                type="button"
                className={s.undo}
                onClick={() => {
                  toast.undo!();
                  setToast(null);
                }}
              >
                <Icon name="undo" size={16} />
                Undo
              </button>
            )}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}
